/**
 * Convert cron expression to human-readable format
 */
export function formatCronExpression(cron: string): string {
  if (!cron) return "Not configured";

  try {
    // Parse cron expression: "minute hour day month dayOfWeek"
    const parts = cron.trim().split(/\s+/);
    if (parts.length < 5) return cron; // Invalid cron, return as-is

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Handle wildcards - if minute or hour is *, return cron as-is (every minute/hour)
    if (minute === "*" || hour === "*") {
      return cron;
    }

    // Convert hour to 12-hour format with AM/PM
    const hourNum = parseInt(hour, 10);
    const minuteNum = parseInt(minute, 10);

    // Validate parsed numbers
    if (isNaN(hourNum) || isNaN(minuteNum)) {
      return cron;
    }

    const period = hourNum >= 12 ? "PM" : "AM";
    const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    const timeStr = `${hour12}:${minuteNum.toString().padStart(2, "0")} ${period}`;

    // Daily: "* * *" or specific days
    if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
      return `Daily at ${timeStr}`;
    }

    // Weekly: specific days of week
    if (dayOfMonth === "*" && month === "*" && dayOfWeek !== "*") {
      const days = parseDaysOfWeek(dayOfWeek);
      return `Weekly on ${days} at ${timeStr}`;
    }

    // Monthly: specific day of month.
    if (dayOfMonth !== "*" && month === "*" && dayOfWeek === "*") {
      const dayNum = parseInt(dayOfMonth, 10);
      if (isNaN(dayNum)) return cron;
      const daySuffix = getDaySuffix(dayNum);
      return `Monthly on the ${dayNum}${daySuffix} at ${timeStr}`;
    }

    // Yearly: specific month and day
    if (dayOfMonth !== "*" && month !== "*") {
      const monthNum = parseInt(month, 10);
      const monthName = getMonthName(monthNum);
      const dayNum = parseInt(dayOfMonth, 10);
      const daySuffix = getDaySuffix(dayNum);
      return `Yearly on ${monthName} ${dayNum}${daySuffix} at ${timeStr}`;
    }

    // Fallback: return the cron expression
    return cron;
  } catch (error) {
    console.error("Failed to parse cron expression:", cron, error);
    return cron;
  }
}

function parseDaysOfWeek(dayOfWeek: string): string {
  const dayMap: Record<string, string> = {
    "0": "Sun",
    "1": "Mon",
    "2": "Tue",
    "3": "Wed",
    "4": "Thu",
    "5": "Fri",
    "6": "Sat",
  };

  const days = dayOfWeek.split(",").map(d => dayMap[d.trim()] || d);

  if (days.length === 1) return days[0];
  if (days.length === 2) return days.join(" and ");

  const lastDay = days.pop();
  return `${days.join(", ")}, and ${lastDay}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function getMonthName(month: number): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1] || `Month ${month}`;
}
